import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileUp, Loader2, XCircle } from 'lucide-react';
import type { ImportPreview, ImportResult } from '@afiliados/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { importsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtBytes, fmtDay, fmtInt, fmtMoney } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Importação em três passos: escolher o arquivo → conferir o que foi lido (período, totais,
 * avisos, conflitos) → importar. O arquivo sobe duas vezes (prévia e importação): são poucos MB
 * e isso evita guardar arquivo temporário no servidor.
 */
export function ImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fields = { periodStart: periodStart || undefined, periodEnd: periodEnd || undefined };
  const previewMut = useMutation({
    mutationFn: (f: File) => importsApi.preview(f, fields),
    onSuccess: (p) => {
      setPreview(p);
      if (p.periodStart) setPeriodStart(p.periodStart);
      if (p.periodEnd) setPeriodEnd(p.periodEnd);
    },
  });
  const importMut = useMutation({
    mutationFn: ({ f, replace }: { f: File; replace: boolean }) =>
      importsApi.create(f, { ...fields, replace }),
    onSuccess: (r) => {
      setResult(r);
      for (const key of ['imports', 'reports', 'dashboard', 'affiliates', 'products', 'months']) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });

  const reset = () => {
    setFile(null);
    setPreview(null);
    setPeriodStart('');
    setPeriodEnd('');
    setResult(null);
    previewMut.reset();
    importMut.reset();
  };
  /** Fechar por qualquer caminho (botões, Esc, clique fora) volta o diálogo ao primeiro passo. */
  const close = () => {
    reset();
    onOpenChange(false);
  };
  const pick = (f: File | null) => {
    reset();
    setFile(f);
    if (f) previewMut.mutate(f);
  };
  const errMsg = (e: unknown) =>
    e instanceof ApiError ? e.message : 'Falha ao processar o arquivo';
  const canImport =
    !!preview && !preview.errors.length && !preview.duplicateOf && preview.rowCount > 0;
  const hasConflicts = !!preview?.conflicts.length;
  const periodChanged =
    !!preview &&
    (periodStart !== (preview.periodStart ?? '') || periodEnd !== (preview.periodEnd ?? ''));

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importar relatório da BuyGoods</DialogTitle>
          <DialogDescription>
            O arquivo exportado em Reports → Affiliates (.xls, .xlsx ou .csv). O período vem do
            título do relatório; um arquivo por mês, por período ou por dia.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors',
                drag && 'border-primary bg-primary/5',
              )}
              onDragOver={(e) => {
                e.preventDefault();
                setDrag(true);
              }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDrag(false);
                pick(e.dataTransfer.files[0] ?? null);
              }}
              onClick={() => inputRef.current?.click()}
            >
              <FileUp className="size-8 text-muted-foreground" />
              {file ? (
                <p className="text-sm font-medium">
                  {file.name} <span className="text-muted-foreground">({fmtBytes(file.size)})</span>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Arraste o relatório aqui ou clique para selecionar
                </p>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                className="hidden"
                onChange={(e) => {
                  pick(e.target.files?.[0] ?? null);
                  // Permite escolher o mesmo arquivo de novo (o onChange não dispara com valor igual).
                  e.target.value = '';
                }}
              />
            </div>

            {previewMut.isPending && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Lendo o arquivo…
              </p>
            )}
            {previewMut.error && (
              <Alert variant="destructive">
                <XCircle className="size-4" />
                <AlertTitle>Não foi possível ler</AlertTitle>
                <AlertDescription>{errMsg(previewMut.error)}</AlertDescription>
              </Alert>
            )}

            {preview && (
              <div className="space-y-3">
                <div className="grid gap-3 rounded-lg border p-3 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Título do relatório</p>
                    <p className="font-medium">{preview.title ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Gerado na BuyGoods em</p>
                    <p className="font-medium">
                      {preview.reportDate ? fmtDay(preview.reportDate) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Linhas · afiliados · produtos</p>
                    <p className="font-medium tabular-nums">
                      {fmtInt(preview.rowCount)} · {fmtInt(preview.affiliateCount)} ·{' '}
                      {fmtInt(preview.productCount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Bruto · líquido · pedidos</p>
                    <p className="font-medium tabular-nums">
                      {fmtMoney(preview.totals.grossSales)} · {fmtMoney(preview.totals.netSales)} ·{' '}
                      {fmtInt(preview.totals.orders)}
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="imp-start" className="text-xs">
                      Período — início
                    </Label>
                    <Input
                      id="imp-start"
                      type="date"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="imp-end" className="text-xs">
                      Período — fim
                    </Label>
                    <Input
                      id="imp-end"
                      type="date"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    {preview.periodSource === 'detected' &&
                      'Lido do título do relatório. Altere só se o título estiver errado.'}
                    {preview.periodSource === 'manual' && 'Período informado à mão.'}
                    {preview.periodSource === 'missing' &&
                      'O arquivo não declara o período: informe as datas.'}
                    {periodChanged && file && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 pl-1"
                        onClick={() => previewMut.mutate(file)}
                      >
                        Reconferir com estas datas
                      </Button>
                    )}
                  </p>
                </div>

                {preview.errors.map((e) => (
                  <Alert key={e} variant="destructive">
                    <XCircle className="size-4" />
                    <AlertDescription>{e}</AlertDescription>
                  </Alert>
                ))}
                {preview.duplicateOf && (
                  <Alert variant="destructive">
                    <XCircle className="size-4" />
                    <AlertTitle>Este arquivo já foi importado</AlertTitle>
                    <AlertDescription>
                      É o mesmo arquivo de{' '}
                      <button
                        type="button"
                        className="underline"
                        onClick={() => {
                          const id = preview.duplicateOf!.id;
                          close();
                          navigate(`/importacoes/${id}`);
                        }}
                      >
                        {preview.duplicateOf.fileName}
                      </button>{' '}
                      ({fmtDay(preview.duplicateOf.periodStart)} –{' '}
                      {fmtDay(preview.duplicateOf.periodEnd)}). Nada a fazer.
                    </AlertDescription>
                  </Alert>
                )}
                {preview.warnings.length > 0 && (
                  <Alert>
                    <AlertTriangle className="size-4 text-amber-600" />
                    <AlertTitle>Avisos da leitura</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc pl-4 text-xs">
                        {preview.warnings.slice(0, 8).map((w) => (
                          <li key={w}>{w}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
                {hasConflicts && !preview.duplicateOf && (
                  <Alert>
                    <AlertTriangle className="size-4 text-amber-600" />
                    <AlertTitle>
                      O período cruza {preview.conflicts.length} importação(ões) ativa(s)
                    </AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc pl-4 text-xs">
                        {preview.conflicts.map((c) => (
                          <li key={c.id}>
                            {c.fileName} · {fmtDay(c.periodStart)} – {fmtDay(c.periodEnd)} ·{' '}
                            {fmtInt(c.rowCount)} linhas · bruto {fmtMoney(c.totals.grossSales)}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1 text-xs">
                        Para não somar duas vezes o mesmo dia, duas importações ativas nunca cruzam
                        período. "Importar e substituir" deixa as listadas como substituídas
                        (continuam no histórico).
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
            {importMut.error && (
              <Alert variant="destructive">
                <XCircle className="size-4" />
                <AlertTitle>Falha ao importar</AlertTitle>
                <AlertDescription>{errMsg(importMut.error)}</AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <Alert>
            <CheckCircle2 className="size-4 text-success" />
            <AlertTitle>Importação concluída</AlertTitle>
            <AlertDescription>
              {fmtDay(result.import.periodStart)} – {fmtDay(result.import.periodEnd)} ·{' '}
              {fmtInt(result.import.rowCount)} linhas · bruto{' '}
              {fmtMoney(result.import.totals.grossSales)} · líquido{' '}
              {fmtMoney(result.import.totals.netSales)}
              {result.affiliatesCreated > 0 && ` · ${result.affiliatesCreated} afiliado(s) novo(s)`}
              {result.superseded.length > 0 &&
                ` · substituiu ${result.superseded.length} importação(ões)`}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  const id = result.import.id;
                  close();
                  navigate(`/importacoes/${id}`);
                }}
              >
                Ver importação
              </Button>
              <Button onClick={close}>Fechar</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={close}>
                Cancelar
              </Button>
              <Button
                disabled={!file || !canImport || importMut.isPending}
                variant={hasConflicts ? 'destructive' : 'default'}
                onClick={() => file && importMut.mutate({ f: file, replace: hasConflicts })}
              >
                {importMut.isPending && <Loader2 className="animate-spin" />}
                {hasConflicts ? 'Importar e substituir' : 'Importar'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
